/**
 * Metodología ESCENA + CAMPO — tokens visuales por modalidad.
 * Nombres de producto: GAME_MODE_CATALOG (core).
 */
import type { GameMode, RoomPhase } from '@interactive-room';
import { GAME_MODE_CATALOG } from '@interactive-room';

export const BRAND = {
  name: 'Edusico Sala',
  tagline: 'Tu asignatura. Tu clase. En vivo.',
  manifesto:
    'Elige la materia que te toca hoy, arma la actividad en minutos y llévala a la sala. Sin jerga técnica — como preparar clase de verdad.',
} as const;

/** Pasos de valor para landing y onboarding */
export const HOW_IT_WORKS = [
  {
    step: '1',
    title: 'Elige tu asignatura',
    detail: 'Matemáticas, Música, Lenguaje… lo que te toque hoy con ese curso.',
  },
  {
    step: '2',
    title: 'Diseña o genera con IA',
    detail: 'Casos de uso listos o pide una actividad en lenguaje natural.',
  },
  {
    step: '3',
    title: 'Proyecta y la clase responde',
    detail: 'Código en pantalla. Celulares conectados. Energía de aula real.',
  },
] as const;

export const REFERENCE_APPS = [
  { name: 'Mentimeter', trait: 'Opinión anónima en tiempo real' },
  { name: 'Kahoot!', trait: 'Energía competitiva en la sala' },
  { name: 'Slido', trait: 'Preguntas y votos sin fricción' },
  { name: 'Padlet', trait: 'Murales colaborativos visuales' },
] as const;

export interface ModeVisual {
  id: GameMode;
  label: string;
  verb: string;
  hue: string;
  neon: string;
  deep: string;
  tint: string;
  tintDark: string;
  mesh: string;
  pattern: string;
}

/** Paleta neón Serenity / Swiss por modalidad */
export const MODE_VISUALS: Record<GameMode, ModeVisual> = {
  poll: {
    id: 'poll',
    label: GAME_MODE_CATALOG.poll.displayName,
    verb: GAME_MODE_CATALOG.poll.tagline,
    hue: '#a855f7',
    neon: '#e879f9',
    deep: '#581c87',
    tint: '#f5f0ff',
    tintDark: 'rgba(168, 85, 247, 0.12)',
    mesh: 'radial-gradient(circle at 20% 80%, #7c3aed55 0%, transparent 50%), radial-gradient(circle at 80% 20%, #ec489955 0%, transparent 45%)',
    pattern: 'repeating-linear-gradient(-12deg, transparent, transparent 8px, rgba(232,121,249,0.04) 8px, rgba(232,121,249,0.04) 9px)',
  },
  arena: {
    id: 'arena',
    label: GAME_MODE_CATALOG.arena.displayName,
    verb: GAME_MODE_CATALOG.arena.tagline,
    hue: '#f97316',
    neon: '#fbbf24',
    deep: '#9a3412',
    tint: '#fff7ed',
    tintDark: 'rgba(249, 115, 22, 0.12)',
    mesh: 'radial-gradient(circle at 70% 30%, #ea580c66 0%, transparent 55%), radial-gradient(circle at 10% 90%, #ef444444 0%, transparent 40%)',
    pattern: 'repeating-linear-gradient(90deg, transparent, transparent 24px, rgba(251,191,36,0.05) 24px, rgba(251,191,36,0.05) 25px)',
  },
  coop: {
    id: 'coop',
    label: GAME_MODE_CATALOG.coop.displayName,
    verb: GAME_MODE_CATALOG.coop.tagline,
    hue: '#14b8a6',
    neon: '#5eead4',
    deep: '#115e59',
    tint: '#ecfdf5',
    tintDark: 'rgba(20, 184, 166, 0.12)',
    mesh: 'radial-gradient(circle at 50% 0%, #0d948866 0%, transparent 60%), radial-gradient(circle at 100% 100%, #06b6d444 0%, transparent 50%)',
    pattern: 'repeating-conic-gradient(from 0deg at 50% 50%, transparent 0deg 8deg, rgba(94,234,212,0.03) 8deg 9deg)',
  },
  sandbox: {
    id: 'sandbox',
    label: GAME_MODE_CATALOG.sandbox.displayName,
    verb: GAME_MODE_CATALOG.sandbox.tagline,
    hue: '#0ea5e9',
    neon: '#38bdf8',
    deep: '#0c4a6e',
    tint: '#f0f9ff',
    tintDark: 'rgba(14, 165, 233, 0.12)',
    mesh: 'radial-gradient(circle at 30% 40%, #0284c766 0%, transparent 50%), radial-gradient(circle at 90% 70%, #6366f155 0%, transparent 45%)',
    pattern: 'linear-gradient(0deg, transparent 49%, rgba(56,189,248,0.06) 50%, transparent 51%)',
  },
  order: {
    id: 'order',
    label: GAME_MODE_CATALOG.order.displayName,
    verb: GAME_MODE_CATALOG.order.tagline,
    hue: '#8b5cf6',
    neon: '#c084fc',
    deep: '#5b21b6',
    tint: '#f5f3ff',
    tintDark: 'rgba(139, 92, 246, 0.12)',
    mesh: 'radial-gradient(circle at 20% 30%, #8b5cf655 0%, transparent 55%), radial-gradient(circle at 80% 70%, #a855f744 0%, transparent 50%)',
    pattern: 'repeating-linear-gradient(90deg, transparent, transparent 20px, rgba(192,132,252,0.05) 20px, rgba(192,132,252,0.05) 21px)',
  },
  point: {
    id: 'point',
    label: GAME_MODE_CATALOG.point.displayName,
    verb: GAME_MODE_CATALOG.point.tagline,
    hue: '#0ea5e9',
    neon: '#38bdf8',
    deep: '#0369a1',
    tint: '#f0f9ff',
    tintDark: 'rgba(14, 165, 233, 0.12)',
    mesh: 'radial-gradient(circle at 50% 50%, #ef444433 0%, transparent 60%), radial-gradient(circle at 30% 80%, #0ea5e944 0%, transparent 50%)',
    pattern: 'radial-gradient(circle at center, rgba(56,189,248,0.06) 1px, transparent 1px)',
  },
  wall: {
    id: 'wall',
    label: GAME_MODE_CATALOG.wall.displayName,
    verb: GAME_MODE_CATALOG.wall.tagline,
    hue: '#f59e0b',
    neon: '#fbbf24',
    deep: '#b45309',
    tint: '#fffbeb',
    tintDark: 'rgba(245, 158, 11, 0.12)',
    mesh: 'radial-gradient(circle at 15% 85%, #22c55e33 0%, transparent 40%), radial-gradient(circle at 85% 15%, #ef444433 0%, transparent 40%)',
    pattern: 'repeating-linear-gradient(-45deg, transparent, transparent 12px, rgba(251,191,36,0.04) 12px, rgba(251,191,36,0.04) 13px)',
  },
  completar: {
    id: 'completar',
    label: GAME_MODE_CATALOG.completar.displayName,
    verb: GAME_MODE_CATALOG.completar.tagline,
    hue: '#6A4C93',
    neon: '#b794f6',
    deep: '#44337a',
    tint: '#f5f0ff',
    tintDark: 'rgba(106, 76, 147, 0.14)',
    mesh: 'radial-gradient(circle at 25% 70%, #6A4C9344 0%, transparent 55%)',
    pattern: 'repeating-linear-gradient(0deg, transparent, transparent 10px, rgba(183,148,246,0.05) 10px, rgba(183,148,246,0.05) 11px)',
  },
  desarrollo: {
    id: 'desarrollo',
    label: GAME_MODE_CATALOG.desarrollo.displayName,
    verb: GAME_MODE_CATALOG.desarrollo.tagline,
    hue: '#5b7c99',
    neon: '#94b8d4',
    deep: '#334155',
    tint: '#f1f5f9',
    tintDark: 'rgba(91, 124, 153, 0.14)',
    mesh: 'radial-gradient(circle at 80% 20%, #5b7c9944 0%, transparent 50%)',
    pattern: 'linear-gradient(90deg, transparent 49%, rgba(148,184,212,0.06) 50%, transparent 51%)',
  },
  vincula: {
    id: 'vincula',
    label: GAME_MODE_CATALOG.vincula.displayName,
    verb: GAME_MODE_CATALOG.vincula.tagline,
    hue: '#9f7aea',
    neon: '#d6bcfa',
    deep: '#553c9a',
    tint: '#faf5ff',
    tintDark: 'rgba(159, 122, 234, 0.14)',
    mesh: 'radial-gradient(circle at 40% 40%, #9f7aea44 0%, transparent 55%)',
    pattern: 'repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(214,188,250,0.05) 8px, rgba(214,188,250,0.05) 9px)',
  },
  agrupa: {
    id: 'agrupa',
    label: GAME_MODE_CATALOG.agrupa.displayName,
    verb: GAME_MODE_CATALOG.agrupa.tagline,
    hue: '#48bb78',
    neon: '#9ae6b4',
    deep: '#276749',
    tint: '#f0fff4',
    tintDark: 'rgba(72, 187, 120, 0.14)',
    mesh: 'radial-gradient(circle at 60% 80%, #48bb7844 0%, transparent 50%)',
    pattern: 'repeating-linear-gradient(-12deg, transparent, transparent 14px, rgba(154,230,180,0.05) 14px, rgba(154,230,180,0.05) 15px)',
  },
  dictado: {
    id: 'dictado',
    label: GAME_MODE_CATALOG.dictado.displayName,
    verb: GAME_MODE_CATALOG.dictado.tagline,
    hue: '#805ad5',
    neon: '#d6bcfa',
    deep: '#553c9a',
    tint: '#faf5ff',
    tintDark: 'rgba(128, 90, 213, 0.14)',
    mesh: 'radial-gradient(circle at 30% 40%, #805ad544 0%, transparent 55%)',
    pattern: 'repeating-linear-gradient(0deg, transparent, transparent 10px, rgba(214,188,250,0.05) 10px, rgba(214,188,250,0.05) 11px)',
  },
  anatomia: {
    id: 'anatomia',
    label: GAME_MODE_CATALOG.anatomia.displayName,
    verb: GAME_MODE_CATALOG.anatomia.tagline,
    hue: '#6b46c1',
    neon: '#b794f4',
    deep: '#44337a',
    tint: '#f7f0ff',
    tintDark: 'rgba(107, 70, 193, 0.14)',
    mesh: 'radial-gradient(circle at 70% 30%, #6b46c144 0%, transparent 50%)',
    pattern: 'repeating-linear-gradient(90deg, transparent, transparent 12px, rgba(183,148,244,0.05) 12px, rgba(183,148,244,0.05) 13px)',
  },
  rhythm_canvas: {
    id: 'rhythm_canvas',
    label: GAME_MODE_CATALOG.rhythm_canvas.displayName,
    verb: GAME_MODE_CATALOG.rhythm_canvas.tagline,
    hue: '#7c3aed',
    neon: '#c4b5fd',
    deep: '#5b21b6',
    tint: '#f5f3ff',
    tintDark: 'rgba(124, 58, 237, 0.16)',
    mesh: 'radial-gradient(circle at 50% 20%, #7c3aed44 0%, transparent 55%)',
    pattern: 'repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(196,181,253,0.06) 8px, rgba(196,181,253,0.06) 9px)',
  },
  melody_staff: {
    id: 'melody_staff',
    label: GAME_MODE_CATALOG.melody_staff.displayName,
    verb: GAME_MODE_CATALOG.melody_staff.tagline,
    hue: '#6366f1',
    neon: '#a5b4fc',
    deep: '#4338ca',
    tint: '#eef2ff',
    tintDark: 'rgba(99, 102, 241, 0.14)',
    mesh: 'radial-gradient(circle at 40% 60%, #6366f144 0%, transparent 50%)',
    pattern: 'repeating-linear-gradient(0deg, transparent, transparent 6px, rgba(165,180,252,0.05) 6px, rgba(165,180,252,0.05) 7px)',
  },
  ear_training: {
    id: 'ear_training',
    label: GAME_MODE_CATALOG.ear_training.displayName,
    verb: GAME_MODE_CATALOG.ear_training.tagline,
    hue: '#9333ea',
    neon: '#d8b4fe',
    deep: '#6b21a8',
    tint: '#faf5ff',
    tintDark: 'rgba(147, 51, 234, 0.14)',
    mesh: 'radial-gradient(circle at 80% 40%, #9333ea44 0%, transparent 55%)',
    pattern: 'repeating-linear-gradient(-45deg, transparent, transparent 10px, rgba(216,180,254,0.05) 10px, rgba(216,180,254,0.05) 11px)',
  },
};

export const PHASE_STRIP: Record<
  RoomPhase,
  { label: string; description: string; color: string }
> = {
  LOBBY: { label: 'Lobby', color: '#8b5cf6', description: 'Esperando conexiones' },
  PREPARING: { label: 'Prep', color: '#3b82f6', description: 'Cargando actividad' },
  ACTIVE: { label: 'En juego', color: '#166534', description: 'Interacción en curso' },
  RESULTS: { label: 'Resultados', color: '#10b981', description: 'Agregando respuestas' },
  PODIUM: { label: 'Podio', color: '#eab308', description: 'Celebración final' },
  ENDED: { label: 'Fin', color: '#64748b', description: 'Sesión cerrada' },
};
